import * as React from "react";
import { useState, useEffect, useRef } from "react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Calendar as CalendarIcon, Clock, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";

export interface DateTimePickerProps {
  value: string; // Expected format: YYYY-MM-DDTHH:mm
  onChange: (value: string) => void;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  variant?: "default" | "panel";
}

export function parseDateTime(value: string | undefined | null) {
  if (!value) return { date: undefined, time: "" };
  const [datePart, timePart] = value.split("T");
  if (!datePart) return { date: undefined, time: "" };

  const [year, month, day] = datePart.split("-").map(Number);
  const dateObj = new Date(year, month - 1, day);
  const timeStr = timePart ? timePart.slice(0, 5) : "";
  return { date: dateObj, time: timeStr };
}

export function serializeDateTime(date: Date | undefined, time: string) {
  if (!date) return "";
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  const timeStr = time || "00:00";
  return `${yyyy}-${mm}-${dd}T${timeStr}`;
}

export function DateTimePicker({
  value,
  onChange,
  className,
  disabled = false,
  placeholder = "Selecione a data",
  variant = "default",
}: DateTimePickerProps) {
  const isPanel = variant === "panel";
  const [localDate, setLocalDate] = useState<Date | undefined>();
  const [localTime, setLocalTime] = useState("");
  const isEditingTimeRef = useRef(false);
  const prevValRef = useRef("");
  const [isOpen, setIsOpen] = useState(false);

  // Synchronize internal state with external value changes
  useEffect(() => {
    if (isEditingTimeRef.current) return;
    const { date, time } = parseDateTime(value);
    setLocalDate(date);
    setLocalTime(time);
    prevValRef.current = time;
  }, [value]);

  const handleDateSelect = (date: Date | undefined) => {
    setLocalDate(date);
    if (date) {
      const timeStr = localTime || "00:00";
      const serialized = serializeDateTime(date, timeStr);
      onChange(serialized);
    } else {
      onChange("");
    }
  };

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    isEditingTimeRef.current = true;
    let val = e.target.value;

    // Allow backspace of colon
    if (prevValRef.current.endsWith(":") && val.length < prevValRef.current.length) {
      val = val.slice(0, -1);
    }

    const clean = val.replace(/[^0-9]/g, "");
    let formatted = clean;
    if (clean.length > 2) {
      formatted = `${clean.slice(0, 2)}:${clean.slice(2, 4)}`;
    }

    // Auto-correct invalid hours
    if (formatted.length >= 2) {
      const hours = Number(formatted.slice(0, 2));
      if (hours > 23) {
        formatted = "23" + formatted.slice(2);
      }
    }
    // Auto-correct invalid minutes
    if (formatted.length === 5) {
      const mins = Number(formatted.slice(3, 5));
      if (mins > 59) {
        formatted = formatted.slice(0, 3) + "59";
      }
    }

    prevValRef.current = formatted;
    setLocalTime(formatted);

    // Propagate only valid values
    if (formatted.length === 5 && localDate) {
      onChange(serializeDateTime(localDate, formatted));
    }
  };

  const handleTimeBlur = () => {
    isEditingTimeRef.current = false;
    let clean = localTime.replace(/\D/g, "");

    if (clean.length === 0) {
      clean = "0000";
    } else if (clean.length === 1) {
      clean = "0" + clean + "00";
    } else if (clean.length === 2) {
      clean = clean + "00";
    } else if (clean.length === 3) {
      clean = clean.slice(0, 2) + "0" + clean.slice(2);
    }

    let h = Number(clean.slice(0, 2));
    let m = Number(clean.slice(2, 4));

    if (h > 23) h = 23;
    if (m > 59) m = 59;

    const formatted = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
    setLocalTime(formatted);

    if (localDate) {
      onChange(serializeDateTime(localDate, formatted));
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant={isPanel ? "secondary" : "outline"}
          disabled={disabled}
          className={cn(
            "w-full justify-between text-left font-normal",
            isPanel
              ? "mt-2 h-12 rounded-2xl bg-secondary/50 px-4 font-medium shadow-none hover:bg-secondary/70 focus-visible:ring-1 focus-visible:ring-primary/30 focus-visible:ring-offset-0 focus-visible:bg-background"
              : "h-10 px-3 rounded-md border border-input bg-background/50 hover:bg-background transition-all focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-muted-foreground/30 focus-visible:border-muted-foreground/30",
            !value && "text-muted-foreground",
            className
          )}
        >
          <div className="flex items-center min-w-0 truncate">
            {!isPanel && <CalendarIcon className="mr-1.5 h-3.5 w-3.5 shrink-0 text-primary" />}
            {localDate ? (
              <span className={cn(
                "truncate",
                isPanel ? "font-display text-sm font-semibold tabular-nums tracking-tight" : "text-xs font-medium",
              )}>
                {format(localDate, "dd/MM/yyyy", { locale: ptBR })}
                <span className={cn("mx-1.5 font-normal", isPanel ? "text-primary" : "text-muted-foreground/50")}>·</span>
                <span className={isPanel ? "tabular-nums" : "font-semibold text-foreground"}>{localTime || "00:00"}</span>
              </span>
            ) : (
              <span className="text-muted-foreground text-xs font-medium truncate">{placeholder}</span>
            )}
          </div>
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0 ml-1.5 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn(
          "w-auto p-0 border border-border/80 bg-background/95 backdrop-blur-md shadow-xl",
          isPanel ? "rounded-[22px]" : "rounded-xl",
        )}
        align="start"
      >
        <Calendar
          mode="single"
          selected={localDate}
          onSelect={handleDateSelect}
          locale={ptBR}
          weekStartsOn={0} // Sunday as first day of the week
          captionLayout="dropdown-buttons"
          fromYear={new Date().getFullYear()}
          toYear={new Date().getFullYear() + 10}
          className="p-3"
          disabled={disabled}
        />
        <div className="border-t border-border/50 px-4 py-3 bg-muted/20 flex items-center justify-between gap-4">
          <span className="flex shrink-0 items-center gap-1.5 text-xs font-semibold text-muted-foreground">
            {!isPanel && <Clock className="h-3.5 w-3.5 text-primary" />}
            Horário do Evento
          </span>
          <div className="flex items-center gap-2">
            <Input
              type="text"
              placeholder="00:00"
              value={localTime}
              onChange={handleTimeChange}
              onBlur={handleTimeBlur}
              disabled={disabled}
              className="w-16 text-center font-mono text-sm h-8 px-2 rounded-lg border-input focus-visible:ring-1 focus-visible:ring-muted-foreground/30 focus-visible:border-muted-foreground/30"
              maxLength={5}
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
